import { ActiveElement, Chart, ChartEvent } from "chart.js";
import {
  Consumption,
  ConsumptionFormatted,
  Dataset,
  FormattedComparativeConsumptions,
  FormattedConsumptionList,
  GetGraphDataComparativeConsumptionsRequestResponse,
  SelectedDownloadData,
} from "../../../core/models/consumptions/consumptions.model";
import theme from "../../../muiTheme";
import { formatStringDate } from "../../dateUtils/formatDate.util";
import {
  formatFirstChartUppercase,
  formatNumber,
} from "../../formatters/formatters.util";
import { BillsConsumptionsFormatted } from "../../../core/models/bills/bills.model";
import { CustomWindow } from "../../../core/models/consumptions/consumptions.model";

const months: string[] = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const initialSelectedDowloadData: SelectedDownloadData = {
  dateFrom: null,
  dateTo: null,
  energyType: [],
  groupBy: "",
  fileType: "",
};

const formatConsumptions = (
  consumptions: Consumption[]
): ConsumptionFormatted[] =>
  consumptions.map(
    ({
      ConsumoActivaTotal,
      ConsumoReactivaTotal,
      FechaFactura,
      PeriodoFacturacion,
      IdContrato,
      IdFactura,
      ActivaP1,
      ActivaP2,
      ActivaP3,
      ActivaP4,
      ActivaP5,
      ActivaP6,
      ReactivaP1,
      ReactivaP2,
      ReactivaP3,
      ReactivaP4,
      ReactivaP5,
      ReactivaP6,
      PeriodoP1,
      PeriodoP2,
      PeriodoP3,
      PeriodoP4,
      PeriodoP5,
      PeriodoP6,
    }) => ({
      totalActiveConsumption: formatNumber(ConsumoActivaTotal),
      totalReactiveConsumption: formatNumber(ConsumoReactivaTotal),
      invoiceDate: formatStringDate(FechaFactura),
      dateFrom: formatStringDate(PeriodoFacturacion.Desde),
      dateTo: formatStringDate(PeriodoFacturacion.Hasta),
      contractId: IdContrato,
      invoicesId: IdFactura,
      activePowers: [
        ActivaP1,
        ActivaP2,
        ActivaP3,
        ActivaP4,
        ActivaP5,
        ActivaP6,
      ],
      reactivePowers: [
        ReactivaP1,
        ReactivaP2,
        ReactivaP3,
        ReactivaP4,
        ReactivaP5,
        ReactivaP6,
      ],
      periodPowers: [
        PeriodoP1,
        PeriodoP2,
        PeriodoP3,
        PeriodoP4,
        PeriodoP5,
        PeriodoP6,
      ],
    })
  );

const formatComparativeConsumptions = (
  graphData: GetGraphDataComparativeConsumptionsRequestResponse[]
): FormattedComparativeConsumptions[] =>
  graphData.map(
    ({ Año: year, Consumo: { totalEnergiaActiva, consumptionList } }) => {
      const formattedConsumptionList: FormattedConsumptionList[] = [];
      const uniquesConsumptionList: Consumption[] = consumptionList
        .filter(
          (consumption, indexNumber, consumptionListArray) =>
            indexNumber ===
            consumptionListArray.findIndex(
              ({ FechaFactura }) => consumption.FechaFactura === FechaFactura
            )
        )
        .map((consumption) => ({
          ...consumption,
          FechaFactura: new Date(consumption.FechaFactura).toLocaleString(
            "es",
            {
              month: "long",
            }
          ),
        }));

      months.forEach((month, indexNumberMonth) => {
        const consumption = uniquesConsumptionList.find(
          ({ FechaFactura }) => month.toLowerCase() === FechaFactura
        );

        if (consumption) {
          const indexFormattedConsumption = formattedConsumptionList?.findIndex(
            ({ invoiceDate }) => invoiceDate === consumption.FechaFactura
          );

          const consumptionFormatted: FormattedConsumptionList =
            formattedComparativeConsumptionsGenerator(
              month,
              year,
              indexNumberMonth,
              uniquesConsumptionList.length,
              consumption
            );

          if (indexFormattedConsumption && indexFormattedConsumption !== -1) {
            const formattedConsumptionKeys = Object.keys(
              consumptionFormatted
            ) as Array<keyof FormattedConsumptionList>;

            return formattedConsumptionKeys.forEach((key) => {
              if (typeof consumptionFormatted[key] === "number") {
                (formattedConsumptionList[indexFormattedConsumption] as any)[
                  key
                ] += consumptionFormatted[key];
              }
            });
          }

          return formattedConsumptionList.push(consumptionFormatted);
        }

        return formattedConsumptionList.push(
          formattedComparativeConsumptionsGenerator(
            month,
            year,
            indexNumberMonth,
            uniquesConsumptionList.length
          )
        );
      });

      return {
        year,
        totalActiveEnergy: totalEnergiaActiva,
        consumptionList: formattedConsumptionList,
      };
    }
  );

const formattedComparativeConsumptionsGenerator = (
  month: string,
  year: string,
  indexNumberMonth: number,
  yearConsumptionsLength: number,
  consumption: Consumption | null = null
) => {
  const emptyValueCondition =
    year === new Date().getFullYear().toString() &&
    indexNumberMonth > yearConsumptionsLength - 1;
  const invoiceDate = consumption?.FechaFactura
    ? formatFirstChartUppercase(consumption.FechaFactura)
    : formatFirstChartUppercase(month);

  const emptyValue: number | null = emptyValueCondition ? null : 0;

  return {
    invoiceDate,
    dateFrom: consumption?.PeriodoFacturacion.Desde || emptyValue,
    dateTo: consumption?.PeriodoFacturacion.Hasta || emptyValue,
    activeP1: consumption?.ActivaP1 || emptyValue,
    activeP2: consumption?.ActivaP2 || emptyValue,
    activeP3: consumption?.ActivaP3 || emptyValue,
    activeP4: consumption?.ActivaP4 || emptyValue,
    activeP5: consumption?.ActivaP5 || emptyValue,
    activeP6: consumption?.ActivaP6 || emptyValue,
    reactiveP1: consumption?.ReactivaP1 || emptyValue,
    reactiveP2: consumption?.ReactivaP2 || emptyValue,
    reactiveP3: consumption?.ReactivaP3 || emptyValue,
    reactiveP4: consumption?.ReactivaP4 || emptyValue,
    reactiveP5: consumption?.ReactivaP5 || emptyValue,
    reactiveP6: consumption?.ReactivaP6 || emptyValue,
    year: year,
    totalActiveConsumption: consumption?.ConsumoActivaTotal || emptyValue,
    totalReactiveConsumption: consumption?.ConsumoReactivaTotal || emptyValue,
  };
};

const getStackedDatasets = (
  consumptionListFormatted: ConsumptionFormatted[]
): Dataset[] =>
  consumptionListFormatted[0].periodPowers.map((period, indexNumber) => ({
    label: period,
    data: consumptionListFormatted.map(
      (consumptionPeriod) => consumptionPeriod.activePowers[indexNumber]
    ),
    backgroundColor: getDatasetsBackgroundColor(indexNumber),
    borderWidth: consumptionListFormatted.map(() => ({
      right: 0,
      left: 0,
      top: 0,
      bottom: 0,
    })),
    borderColor: theme.palette.grey[700],
    borderSkipped: false,
  }));

const getDatasetsBackgroundColor = (periodPosition: number) => {
  switch (periodPosition) {
    case 0:
      return theme.palette.consumptions.period1.main;
    case 1:
      return theme.palette.consumptions.period2.main;
    case 2:
      return theme.palette.consumptions.period3;
    case 3:
      return theme.palette.consumptions.period4;
    case 4:
      return theme.palette.consumptions.period5;
    case 5:
      return theme.palette.consumptions.period6;
    default:
      return "";
  }
};

const getBarPositionX = (chart: Chart, event: ChartEvent): number => {
  const { maxWidth, ticks, left } = chart.scales.x;
  const periodWidth = maxWidth / ticks.length;
  let positionX = 0;
  ticks.forEach((tick, indexNumber) => {
    const begginingX = left + periodWidth * indexNumber;
    const endX = left + periodWidth + periodWidth * indexNumber;
    if ((event.x as number) >= begginingX && (event.x as number) <= endX) {
      positionX = begginingX + periodWidth / 2;
    }
  });

  return positionX;
};

const handleClickBarChart = (elements: ActiveElement[], chart: Chart) => {
  const selectedPeriodIndex = elements[0].index;
  if (chart) {
    const xAxesFonts = chart.options.scales?.x?.ticks?.font as any[];
    const datasets = chart.data.datasets;
    let lastValueIndex = -1;
    let isFirstBlockValue = true;
    xAxesFonts.forEach((tick, indexNumber) => {
      if (indexNumber === selectedPeriodIndex) {
        (xAxesFonts as any)[indexNumber].weight = "800";
        return;
      }
      (xAxesFonts as any)[indexNumber].weight = "400";
    });

    datasets.forEach(({ borderWidth, data }, datasetIndexNumber) => {
      xAxesFonts.forEach((tick, indexNumber) => {
        const value = data[indexNumber];

        if (indexNumber === selectedPeriodIndex && value !== 0) {
          (borderWidth as any)[indexNumber] = {
            left: 3,
            right: 3,
            top: 3,
            ...(isFirstBlockValue && { bottom: 3 }),
          };

          if (lastValueIndex !== -1) {
            (datasets[lastValueIndex].borderWidth as any)[indexNumber] = {
              ...(datasets[lastValueIndex].borderWidth as any)[indexNumber],
              top: 0,
            };
          }

          isFirstBlockValue = false;
          lastValueIndex = datasetIndexNumber;
          return;
        }

        (borderWidth as any)[indexNumber] = {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        };
      });
    });

    chart.update();
  }
};

const handleClickLineChart = (elements: ActiveElement[], chart: Chart) => {
  const selectedPeriodIndex = elements[0].index;

  if (chart) {
    const xAxesFonts = chart.options.scales?.x?.ticks?.font as any[];
    xAxesFonts.forEach((tick, indexNumber) => {
      if (indexNumber === selectedPeriodIndex) {
        (xAxesFonts as any)[indexNumber].weight = "800";
        return;
      }
      (xAxesFonts as any)[indexNumber].weight = "400";
    });

    chart.update();
  }
};

const getBarChart = (
  environment: string,
  chartRef: HTMLCanvasElement,
  consumptionsList: ConsumptionFormatted[] | BillsConsumptionsFormatted[],
  isDesktopViewport: boolean,
  handleClick: (elements: ActiveElement[], event: ChartEvent) => void,
  datasets: Dataset[],
  isStackedChart: boolean
) =>
  new Chart(chartRef, {
    type: "bar",
    data: {
      labels: consumptionsList.map(({ invoiceDate }) => invoiceDate),
      datasets,
    },
    ...(isStackedChart && {
      plugins: [
        {
          id: "legendMargin",
          beforeInit(chart) {
            const fitValue = (chart.legend as any)?.fit;
            if (fitValue) {
              (chart.legend as any).fit = function fit() {
                fitValue.bind(chart.legend)();
                return (this.height += 50);
              };
            }
          },
        },
      ],
    }),
    options: {
      responsive: true,
      aspectRatio: 1,
      maintainAspectRatio: false,
      onClick: function (event, elements, chart) {
        elements.length && handleClick(elements, event);
      },
      onHover: function (event, elements) {
        (event.native?.target as any).style.cursor = elements.length
          ? "pointer"
          : "default";
      },
      datasets: {
        bar: {
          barPercentage: isDesktopViewport ? 0.6 : 0.9,
          maxBarThickness: 65,
        },
      },
      layout: {
        padding: isDesktopViewport
          ? {
              top: 20,
              bottom: 0,
              right: 48,
              left: 20,
            }
          : {
              top: 10,
              bottom: 0,
              right: 6,
              left: 6,
            },
      },
      plugins: {
        legend: {
          align: "start",
          display: isStackedChart,
          title: {
            position: "center",
          },
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxHeight: 9,
            padding: isDesktopViewport ? 42 : 20,
          },
        },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          stacked: isStackedChart,
          offset: true,
          ticks: {
            autoSkip: true,
            maxRotation: 45,
            minRotation: 45,
            font: consumptionsList.map(() => ({
              weight: 400,
            })) as any,
          },
          grid: {
            display: false,
          },
        },
        y: {
          stacked: isStackedChart,
          border: { dash: [5, 5], display: false },
          grid: {
            color: (context) =>
              context.tick.value === 0 ? "transparent" : "rgba(0, 0, 0, 0.1)",
            drawTicks: false,
          },
          ticks: {
            callback: (value) =>
              `${formatNumber(+value)} ${isStackedChart ? (environment === "Gas" ? "m3" : "kWh") : "€"}`,
            padding: 10,
          },
        },
      },
    },
  });

const getLineChart = (
  environment: string,
  chartRef: HTMLCanvasElement,
  comparativeConsumptions: FormattedComparativeConsumptions[],
  isDesktopViewport: boolean,
  handleClick: (elements: ActiveElement[], event: ChartEvent) => void
) =>
  new Chart(chartRef, {
    type: "line",
    data: {
      labels: months,
      datasets: comparativeConsumptions.map(
        ({ year, consumptionList }, indexNumber) => ({
          label: year,
          data: consumptionList.map(
            ({ totalActiveConsumption }) => totalActiveConsumption
          ),
          borderWidth: 2,
          borderColor: !!indexNumber
            ? theme.palette.consumptions.period2.main
            : theme.palette.consumptions.period1.main,
          backgroundColor: !!indexNumber
            ? theme.palette.consumptions.period2.light
            : theme.palette.consumptions.period1.light,
          pointBackgroundColor: !!indexNumber
            ? theme.palette.consumptions.period2.main
            : theme.palette.consumptions.period1.main,
          fill: "origin",
        })
      ),
    },
    plugins: [
      {
        id: "legendMargin",
        beforeInit(chart) {
          const fitValue = (chart.legend as any)?.fit;
          if (fitValue) {
            (chart.legend as any).fit = function fit() {
              fitValue.bind(chart.legend)();
              return (this.height += 50);
            };
          }
        },
      },
    ],
    options: {
      responsive: true,
      aspectRatio: 1,
      maintainAspectRatio: false,
      onClick: function (event, elements, chart) {
        elements.length && handleClick(elements, event);
      },
      onHover: function (event, elements) {
        (event.native?.target as any).style.cursor = elements.length
          ? "pointer"
          : "default";
      },
      datasets: {
        line: {
          pointBorderWidth: 5,
        },
      },
      layout: {
        padding: {
          top: 20,
          bottom: 0,
          right: 48,
          left: 20,
        },
      },
      plugins: {
        legend: {
          align: "start",
          display: true,
          title: {
            position: "center",
          },
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            pointStyleWidth: 8,
            boxHeight: 6,
            padding: isDesktopViewport ? 42 : 20,
          },
        },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          border: { dash: [5, 5] },
          grid: {
            color: (context) => "rgba(0, 0, 0, 0.1)",
            drawTicks: false,
          },
          offset: true,
          ticks: {
            autoSkip: true,
            padding: 16,
            maxRotation: 45,
            minRotation: 45,
            font: months.map(() => ({
              weight: 400,
            })) as any,
          },
        },
        y: {
          border: { dash: [5, 5], display: false },
          grid: {
            color: (context) => {
              const isLastOrFirst =
                context.tick.value ===
                  context.scale.ticks[context.scale.ticks.length - 1].value ||
                context.tick.value === context.scale.ticks[0].value;
              return isLastOrFirst ? "transparent" : "rgba(0, 0, 0, 0.1)";
            },
            drawTicks: false,
          },
          ticks: {
            callback: (value) => `${formatNumber(+value)} ${environment === "Gas" ? "m3" : "kWh"}`,
            padding: 12,
          },
        },
      },
    },
  });

const base64ToBlob = (b64Data: string, contentType = "", sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

const saveFileToAccessibleLocation = (
  fileName: string,
  onSuccess: () => void
) => {
  const downloadsPath = cordova.file.externalRootDirectory + "/Download/";
  const customWindow = window as unknown as CustomWindow;

  customWindow.resolveLocalFileSystemURL(
    cordova.file.dataDirectory + fileName,
    (fileEntry: any) => {
      customWindow.resolveLocalFileSystemURL(
        downloadsPath,
        (dirEntry: any) => {
          fileEntry.moveTo(
            dirEntry,
            fileName,
            (newFileEntry: any) => {
              console.log("File saved to Downloads:", newFileEntry.nativeURL);
              onSuccess();
            },
            (error: any) => {
              console.error(
                "Error moving file:",
                JSON.stringify(error, null, 2)
              );
            }
          );
        },
        (error: any) => {
          console.error(
            "Error accessing Downloads directory:",
            JSON.stringify(error, null, 2)
          );
        }
      );
    },
    (error: any) => {
      console.error(
        "Error finding the original file:",
        JSON.stringify(error, null, 2)
      );
    }
  );
};

export {
  formatConsumptions,
  getStackedDatasets,
  getDatasetsBackgroundColor,
  getBarChart,
  getLineChart,
  handleClickBarChart,
  handleClickLineChart,
  getBarPositionX,
  formatComparativeConsumptions,
  initialSelectedDowloadData,
  base64ToBlob,
  saveFileToAccessibleLocation,
};
